// src/services/claimService.js
import { auth, db } from '../firebase/config';
import { appendActivity } from './workspaceService';
import { buildInitialFlow } from './flowService';
import { uploadBackup } from './backupService';
import { calculateAnalytics } from '../utils/analytics';
import { mergeById } from '../utils/claimAccess';
import { buildCheckSummary } from '../utils/checkSummary';
import { payableToFixes } from '../utils/textFix';
import { isManualDebt } from '../utils/operatorInvoice';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  runTransaction,
  writeBatch
} from 'firebase/firestore';

const CLAIMS_COLLECTION = 'claims'; // Congelada — respaldo. Ver claim_lifecycle_restructure.md.
const CHECKS_COLLECTION = 'checks'; // Modelo nuevo: cheque/pago hijo de un claimRecord.
const CHECK_SUMMARIES_COLLECTION = 'checkSummaries'; // Espejo sin datos de CSR (utils/checkSummary.js)
const OPERATOR_DEBTS_COLLECTION = 'operatorDebts';
const DEBT_PAYMENT_LOGS = 'debtPaymentLogs';
const SETTINGS_COLLECTION_ROOT = 'config';
const INVOICE_COUNTER_DOC = 'invoiceCounter';

// CONSTANTES SEPARADAS
const CSR_PERCENTAGE = 10;
export const OPERATOR_FEE = 5;

// Obtener email del usuario de forma segura
const getCurrentUserEmail = () => {
  try {
    const user = auth.currentUser;
    const email = user?.email || 'system@csrclaims.com';
    return email;
  } catch (error) {
    console.warn('⚠️ Error getting current user email:', error);
    return 'system@csrclaims.com';
  }
};

// Obtener el último número de invoice
export const getLastInvoiceNumber = async () => {
  try {
    const claims = await getAllClaims();
    if (claims.length === 0) {
      return '100';
    }
    
    const invoiceNumbers = claims
      .map(claim => parseInt(claim.invoiceNumber))
      .filter(num => !isNaN(num) && num > 0);
    
    if (invoiceNumbers.length === 0) {
      return '100';
    }
    
    const maxNumber = Math.max(...invoiceNumbers);
    const nextNumber = maxNumber + 1;
    return nextNumber.toString();
  } catch (error) {
    console.error('Error getting last invoice number:', error);
    return '100';
  }
};

// Reserva atómica del siguiente número de invoice. getLastInvoiceNumber()
// de arriba solo sirve para MOSTRAR una sugerencia mientras se llena el
// formulario — dos admins creando un reclamo al mismo tiempo podían
// terminar calculando el mismo "siguiente" número ahí, y como
// createOperatorDebtForClaim deduplica por invoiceNumber, el segundo
// reclamo se quedaba sin su deuda de operador, en silencio. Este
// contador usa una transacción de Firestore para que el número
// realmente usado al crear el reclamo nunca se repita.
// Versión para usar DENTRO de una transacción que ya leyó todo lo suyo: lee
// el contador y lo avanza en la misma transacción, de modo que el número
// solo se consume si el resto de la transacción también se confirma (así
// no quedan huecos en la numeración).
// Debe llamarse después de todas las lecturas de la transacción.
export const nextInvoiceNumberIn = async transaction => {
  const counterRef = doc(db, SETTINGS_COLLECTION_ROOT, INVOICE_COUNTER_DOC);
  const counterSnap = await transaction.get(counterRef);
  let current;
  if (counterSnap.exists() && typeof counterSnap.data().current === 'number') {
    current = counterSnap.data().current;
  } else {
    // Primera vez que se usa el contador: arrancar desde el mayor
    // invoiceNumber histórico para no chocar con datos ya existentes.
    const claims = await getAllClaims({ strict: true });
    const invoiceNumbers = claims
      .map(claim => parseInt(claim.invoiceNumber, 10))
      .filter(num => !isNaN(num) && num > 0);
    current = invoiceNumbers.length > 0 ? Math.max(...invoiceNumbers) : 99;
  }
  const nextValue = current + 1;
  transaction.set(counterRef, { current: nextValue, updatedAt: Timestamp.now() }, { merge: true });
  return nextValue.toString().padStart(3, '0');
};

export const reserveNextInvoiceNumber = async () => runTransaction(db, transaction => nextInvoiceNumberIn(transaction));

// El invoice de un cheque nace la primera vez que se genera (para poder
// enviarlo al PA antes del pago) y queda fijo. Cheques a los que nunca se
// les generó invoice no gastan número. Si ya existe la deuda de operador
// sin número, se le copia el mismo.
export const ensureCheckInvoiceNumber = async checkId => {
  const checkRef = doc(db, CHECKS_COLLECTION, checkId);
  const debtRef = doc(db, OPERATOR_DEBTS_COLLECTION, checkId);
  return runTransaction(db, async transaction => {
    const checkSnap = await transaction.get(checkRef);
    if (!checkSnap.exists()) throw new Error('Check not found');
    const current = checkSnap.data().invoiceNumber;
    if (current) return current;
    const debtSnap = await transaction.get(debtRef);
    const invoiceNumber = await nextInvoiceNumberIn(transaction);
    transaction.update(checkRef, { invoiceNumber, updatedAt: Timestamp.now() });
    if (debtSnap.exists() && !debtSnap.data().invoiceNumber) {
      transaction.update(debtRef, { invoiceNumber });
    }
    return invoiceNumber;
  });
};

// Generar nota para el CRM
const generateNoteForCRM = (claim, claimId) => {
  const date = new Date().toLocaleDateString();
  const payableTo = claim.payableTo || `${claim.clientName} & ${claim.adjusterName} & ${claim.companyName}`;
  
  const adjusterCommission = (claim.amount * (claim.adjusterPercentage || 0)) / 100;
  const csrCommission = (adjusterCommission * (claim.csrPercentage || CSR_PERCENTAGE)) / 100;
  
  return `📋 **CLAIM SUMMARY - ${claimId}** 📋

**Invoice:** ${claim.invoiceNumber}
**Date:** ${claim.checkDate}
**Action Taken:** Check uploaded to attachments/uploads.

**Client:** ${claim.clientName}
**Payable to:** ${payableTo}
**Claim Number:** ${claim.claimNumber}
**Check Amount:** $${(claim.amount || 0).toLocaleString()}

**Breakdown:**
- Adjuster (${claim.adjusterPercentage || 0}%): $${adjusterCommission.toLocaleString()}
- CSR (${claim.csrPercentage || CSR_PERCENTAGE}% of adjuster): $${csrCommission.toLocaleString()}
- Total Collected: $${csrCommission.toLocaleString()}

**Operator Debt (per claim):** $${OPERATOR_FEE}

**CRM Link:** ${claim.crmLink || `https://app.organizedadjusting.com/app/claims/${claim.crmClaimId}`}
**Status:** Pending Collection

---
_Created on: ${date} by CSR Claims Assistant System_`;
};

// Crear deuda del operador
const createOperatorDebtForClaim = async (claim) => {
  try {
    const userEmail = getCurrentUserEmail();
    
    const debtsQuery = query(
      collection(db, OPERATOR_DEBTS_COLLECTION),
      where('invoiceNumber', '==', claim.invoiceNumber)
    );
    const existingDebts = await getDocs(debtsQuery);
    
    if (!existingDebts.empty) {
      const docRef = existingDebts.docs[0];
      const data = docRef.data();
      return { id: docRef.id, ...data };
    }
    
    const debtData = {
      invoiceNumber: claim.invoiceNumber,
      claimId: claim.id || claim.claimId,
      clientName: claim.clientName || 'Unknown',
      checkDate: claim.checkDate || null,
      amount: OPERATOR_FEE,
      paid: false,
      createdAt: Timestamp.now(),
      createdBy: userEmail,
      companyName: claim.companyName || 'Unknown',
      adjusterName: claim.adjusterName || 'Unknown'
    };
    
    const docRef = await addDoc(collection(db, OPERATOR_DEBTS_COLLECTION), debtData);
    return { id: docRef.id, ...debtData };
  } catch (error) {
    console.error('❌ Error creating operator debt:', error);
    return null;
  }
};

// Crear un nuevo reclamo
export const createClaim = async (claimData, userEmail) => {
  try {
    // Defensa en profundidad: aunque el modo automático ahora reserva el
    // número atómicamente (reserveNextInvoiceNumber), el modo manual deja
    // que el usuario escriba cualquier número — esto detecta la colisión
    // en vez de dejar que se pierda la deuda de operador en silencio.
    const dupQuery = query(
      collection(db, CLAIMS_COLLECTION),
      where('invoiceNumber', '==', claimData.invoiceNumber)
    );
    const dupSnapshot = await getDocs(dupQuery);
    if (!dupSnapshot.empty) {
      throw new Error(`El invoice #${claimData.invoiceNumber} ya existe. Elige otro número.`);
    }

    const adjusterCollected = (claimData.amount * claimData.adjusterPercentage) / 100;
    const csrCollected = (adjusterCollected * claimData.csrPercentage) / 100;
    const totalCollected = csrCollected;
    const pending = totalCollected;
    
    const crmLink = `https://app.organizedadjusting.com/app/claims/${claimData.crmClaimId}`;
    const generatedNote = generateNoteForCRM(claimData, claimData.invoiceNumber);
    
    const newClaim = {
      invoiceNumber: claimData.invoiceNumber,
      checkDate: claimData.checkDate,
      companyName: claimData.companyName,
      payableTo: claimData.payableTo,
      clientName: claimData.clientName,
      cause: claimData.cause,
      adjusterName: claimData.adjusterName,
      claimNumber: claimData.claimNumber,
      crmClaimId: claimData.crmClaimId,
      crmLink: crmLink,
      amount: claimData.amount,
      adjusterPercentage: claimData.adjusterPercentage,
      adjusterCollected: adjusterCollected,
      csrPercentage: claimData.csrPercentage,
      csrCollected: csrCollected,
      totalCollected: totalCollected,
      deposit: 0,
      pending: pending,
      paid: false,
      servicePaid: false,
      attachmentsUploaded: false,
      notesCreated: false,
      generatedNote: generatedNote,
      checkAttachment: {
        url: "",
        uploadedAt: null,
        uploadedBy: ""
      },
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: userEmail,
      status: 'pending'
    };
    
    const docRef = doc(collection(db, CLAIMS_COLLECTION));
    const batch = writeBatch(db);
    batch.set(docRef, newClaim);
    appendActivity(batch, { event: 'claim_created', targetId: docRef.id, reference: `Claim ${newClaim.claimNumber || ''} · Invoice ${newClaim.invoiceNumber || ''}` });
    await batch.commit();
    const claimWithId = { id: docRef.id, ...newClaim };

    // La deuda de operador ya NO se crea acá. Un claim recién creado es
    // solo un reclamo acumulado, no un invoice — se convierte en invoice
    // (y genera deuda de operador) recién cuando el ajustador lo paga, ver
    // markAsPaid() más abajo.

    return claimWithId;
  } catch (error) {
    console.error('❌ ERROR EN createClaim:', error);
    throw error;
  }
};

// Paso 3 de la restructuración (ver claim_lifecycle_restructure.md): al
// firmarse un Closing Statement, se crea el "check" (cheque/pago hijo) con
// los valores aproximados acordados — el monto/porcentajes reales del
// cheque físico se corrigen después, al recibirlo, editando este mismo
// documento. Escribe en `checks`, NUNCA en la colección `claims` vieja
// (congelada como respaldo).
export const createClaimFromStatement = async (statement, userEmail) => {
  // Idempotencia: submitSignature (ClosingStatements.jsx) sube el PDF,
  // marca el statement firmado y crea el cheque en tres pasos separados —
  // si el paso 3 falla y el usuario reintenta, los pasos 1-2 ya son
  // no-ops, pero sin esto un segundo intento crearía un cheque duplicado
  // (con OTRO invoiceNumber reservado) para el mismo statement ya firmado.
  if (statement.id) {
    const existing = await getDocs(query(collection(db, CHECKS_COLLECTION), where('closingStatementId', '==', statement.id)));
    if (!existing.empty) {
      const docSnap = existing.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    }
  }

  // El documento de closingStatement guarda el monto como `checkAmount`
  // (ver ClosingStatements.jsx), no `amount` — este mismatch hacía que TODO
  // cheque nuevo naciera en $0/0%, sin importar lo que se llenara en el
  // formulario (hallazgo P2 del audit del 2026-09-11). `amount` se deja
  // como fallback por si algún día se llama con un objeto ya normalizado.
  const amount = statement.checkAmount ?? statement.amount ?? 0;
  const adjusterPercentage = statement.adjusterPercentage || 0;
  const csrPercentage = statement.csrPercentage || 0;
  const adjusterCollected = (amount * adjusterPercentage) / 100;
  const csrCollected = (adjusterCollected * csrPercentage) / 100;
  const totalCollected = csrCollected;

  // Desde 2026-10-12 (decisión del propietario) el invoice se reserva cuando
  // el cheque queda pagado (markCheckPaid), no al crearlo: así no se gastan
  // números en cheques que luego se borran o nunca se pagan.
  const invoiceNumber = statement.invoiceNumber || '';

  const newCheck = {
    claimRecordId: statement.claimRecordId || statement.linkedClaimId || null,
    closingStatementId: statement.id,
    invoiceNumber,
    checkDate: statement.checkDate || '',
    companyName: statement.companyName || '',
    insuranceCompanyName: statement.insuranceCompanyName || '',
    payableTo: statement.payableTo || '',
    clientName: statement.clientName || '',
    clientEmail: statement.clientEmail || '',
    clientPhone: statement.clientPhone || '',
    clientAddress: statement.clientAddress || '',
    policyNumber: statement.policyNumber || '',
    incidentDate: statement.incidentDate || '',
    cause: statement.cause || '',
    adjusterName: statement.adjusterName || '',
    claimNumber: statement.claimNumber || '',
    crmClaimId: statement.crmClaimId || '',
    // Se copia acá (en vez de exponer la colección closingStatements al
    // portal de ajustadores) para que el ajustador pueda abrir el documento
    // enviado a firmar directo desde /claim-status sin más permisos ni
    // backend nuevo (esa vía nunca llegó a exponerse a los ajustadores).
    // `docusignEnvelopeUrl` es el nombre antiguo del campo (datos previos).
    signatureUrl: statement.signatureUrl || statement.docusignEnvelopeUrl || '',
    amount,
    adjusterPercentage,
    adjusterCollected,
    csrPercentage,
    csrCollected,
    totalCollected,
    deposit: 0,
    pending: totalCollected,
    paid: false,
    checkAttachment: { storageKey: '', uploadedAt: null, uploadedBy: '' },
    crmUploadConfirmed: false,
    paymentHistory: [],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: userEmail,
    status: 'pending'
  };

  const docRef = doc(collection(db, CHECKS_COLLECTION));
  const batch = writeBatch(db);
  batch.set(docRef, newCheck);
  batch.set(doc(db, CHECK_SUMMARIES_COLLECTION, docRef.id), buildCheckSummary(newCheck));
  appendActivity(batch, { event: 'check_created', targetId: docRef.id, reference: `Claim ${newCheck.claimNumber || ''} · Invoice ${newCheck.invoiceNumber || ''}` });
  await batch.commit();
  return { id: docRef.id, ...newCheck };
};

// Closing Statement nuevo (colección `closings`, closingService.js) → cheque
// del cobro de CSR. Cuando el Closing queda FIRMADO, nace el cheque con los
// valores de ese Closing (monto y % del PA) en la etapa "Valores reales",
// y desde ahí sigue el cobro de CSR de siempre (ReceiveCheck → backup/OA →
// CheckPayment → archivo). Idempotente por `closingId`: reintentar no
// duplica el cheque ni reserva otro invoice.
export const createCheckFromClosing = async (closing, claimRecord, userEmail) => {
  const existing = await getDocs(query(collection(db, CHECKS_COLLECTION), where('closingId', '==', closing.id)));
  if (!existing.empty) {
    const docSnap = existing.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  }
  const amount = Number(closing.checkAmount) || 0;
  const adjusterPercentage = Number(closing.feePercent) || 0;
  const csrPercentage = CSR_PERCENTAGE;
  const adjusterCollected = (amount * adjusterPercentage) / 100;
  const csrCollected = (adjusterCollected * csrPercentage) / 100;
  const invoiceNumber = ''; // se reserva al quedar pagado (markCheckPaid)
  const now = Timestamp.now();
  const newCheck = {
    claimRecordId: claimRecord.id,
    closingId: closing.id,
    closingStatementId: null,
    invoiceNumber,
    checkNumber: closing.checkNumber || '',
    checkDate: closing.checkDate || '',
    paymentType: closing.paymentType || '',
    companyName: claimRecord.companyName || '',
    insuranceCompanyName: claimRecord.insuranceCompanyName || '',
    payableTo: '',
    clientName: claimRecord.clientName || '',
    clientEmail: claimRecord.clientEmail || '',
    clientPhone: claimRecord.clientPhone || '',
    clientAddress: claimRecord.clientAddress || '',
    policyNumber: claimRecord.policyNumber || '',
    incidentDate: claimRecord.incidentDate || '',
    cause: claimRecord.cause || '',
    adjusterName: claimRecord.adjusterName || '',
    claimNumber: claimRecord.claimNumber || '',
    crmClaimId: claimRecord.crmClaimId || '',
    signatureUrl: closing.signatureUrl || closing.docusignUrl || '',
    amount,
    adjusterPercentage,
    adjusterCollected,
    csrPercentage,
    csrCollected,
    totalCollected: csrCollected,
    deposit: 0,
    pending: csrCollected,
    paid: false,
    // Los valores vienen del Closing firmado, pero el equipo CSR los
    // confirma en "Valores reales" igual que cualquier cheque.
    realValuesConfirmed: false,
    checkAttachment: { storageKey: '', uploadedAt: null, uploadedBy: '' },
    crmUploadConfirmed: false,
    paymentHistory: [],
    createdAt: now,
    updatedAt: now,
    createdBy: userEmail,
    status: 'pending'
  };
  const docRef = doc(collection(db, CHECKS_COLLECTION));
  const batch = writeBatch(db);
  batch.set(docRef, newCheck);
  batch.set(doc(db, CHECK_SUMMARIES_COLLECTION, docRef.id), buildCheckSummary(newCheck));
  appendActivity(batch, { event: 'check_created', targetId: docRef.id, reference: `Claim ${newCheck.claimNumber || ''} · Closing firmado` });
  await batch.commit();
  return { id: docRef.id, ...newCheck };
};

// Fase 4 del documento de pasos del cliente: el cheque existe desde que el
// seguro lo EMITE (paso "Cheque emitido · en camino" de CHECK_FLOW) y desde
// ahí avanza por su propio flujo (ruta de endoso, depósito, breakdown,
// Closing, desembolso). Es la única forma de crear un cheque nuevo; reserva
// el invoice igual que siempre para el cobro interno de CSR (tramo B).
const commissionFields = (amount, adjusterPercentage) => {
  const adjusterCollected = (amount * adjusterPercentage) / 100;
  const csrCollected = (adjusterCollected * CSR_PERCENTAGE) / 100;
  return { amount, adjusterPercentage, adjusterCollected, csrPercentage: CSR_PERCENTAGE, csrCollected, totalCollected: csrCollected, pending: csrCollected };
};

// Datos del cheque que se pueden editar desde su tarjeta.
export const ISSUED_CHECK_KEYS = ['amount', 'checkNumber', 'checkDate', 'paymentType', 'payableTo', 'hasMortgage', 'adjusterPercentage'];

export const registerIssuedCheck = async (claimRecord, data, userEmail) => {
  const invoiceNumber = ''; // se reserva al quedar pagado (markCheckPaid)
  const now = Timestamp.now();
  const newCheck = {
    claimRecordId: claimRecord.id,
    closingStatementId: null,
    invoiceNumber,
    checkNumber: (data.checkNumber || '').trim(),
    checkDate: data.checkDate || '',
    paymentType: data.paymentType || '',
    payableTo: (data.payableTo || '').trim(),
    hasMortgage: Boolean(data.hasMortgage),
    companyName: claimRecord.companyName || '',
    insuranceCompanyName: claimRecord.insuranceCompanyName || '',
    clientName: claimRecord.clientName || '',
    clientEmail: claimRecord.clientEmail || '',
    clientPhone: claimRecord.clientPhone || '',
    clientAddress: claimRecord.clientAddress || '',
    policyNumber: claimRecord.policyNumber || '',
    incidentDate: claimRecord.incidentDate || '',
    cause: claimRecord.cause || '',
    adjusterName: claimRecord.adjusterName || '',
    accessNames: claimRecord.accessNames || [],
    claimNumber: claimRecord.claimNumber || '',
    crmClaimId: claimRecord.crmClaimId || '',
    ...commissionFields(Number(data.amount) || 0, Number(data.adjusterPercentage ?? claimRecord.agreedFeePercent) || 0),
    deposit: 0,
    paid: false,
    realValuesConfirmed: false,
    checkAttachment: { storageKey: '', uploadedAt: null, uploadedBy: '' },
    crmUploadConfirmed: false,
    paymentHistory: [],
    flow: buildInitialFlow('check', userEmail),
    createdAt: now,
    updatedAt: now,
    createdBy: userEmail,
    status: 'pending'
  };
  const docRef = doc(collection(db, CHECKS_COLLECTION));
  const batch = writeBatch(db);
  batch.set(docRef, newCheck);
  batch.set(doc(db, CHECK_SUMMARIES_COLLECTION, docRef.id), buildCheckSummary(newCheck));
  appendActivity(batch, { event: 'check_created', targetId: docRef.id, reference: `Claim ${newCheck.claimNumber || ''} · Cheque emitido ${newCheck.checkNumber ? `#${newCheck.checkNumber} ` : ''}`.trim() });
  await batch.commit();
  return { id: docRef.id, ...newCheck };
};

// Editar los datos del cheque (tarjeta del cheque). Si cambian monto o %,
// se recalculan las comisiones mientras el cobro de CSR no se haya pagado.
export const updateIssuedCheck = async (check, changes) => {
  const payload = {};
  ISSUED_CHECK_KEYS.forEach(key => {
    if (!(key in changes)) return;
    if (key === 'hasMortgage') payload[key] = Boolean(changes[key]);
    else if (key === 'amount' || key === 'adjusterPercentage') payload[key] = Number(changes[key]) || 0;
    else payload[key] = String(changes[key] ?? '').trim();
  });
  if (('amount' in payload || 'adjusterPercentage' in payload) && !check.paid && !(Number(check.deposit) > 0)) {
    Object.assign(payload, commissionFields(payload.amount ?? (Number(check.amount) || 0), payload.adjusterPercentage ?? (Number(check.adjusterPercentage) || 0)));
  }
  if (Object.keys(payload).length === 0) return {};
  await updateDoc(doc(db, CHECKS_COLLECTION, check.id), { ...payload, updatedAt: Timestamp.now() });
  // Espejo sin datos de CSR: solo lo que cambió y está en la lista blanca (monto, número, fecha…).
  const summaryPatch = buildCheckSummary(payload);
  if (Object.keys(summaryPatch).length) {
    await setDoc(doc(db, CHECK_SUMMARIES_COLLECTION, check.id), { ...summaryPatch, updatedAt: Timestamp.now() }, { merge: true })
      .catch(error => console.warn('checkSummaries no se pudo actualizar (no crítico):', error));
  }
  return payload;
};

// Paso 4 (ver claim_lifecycle_restructure.md): registrar el pago del CSR
// por ESTE cheque específico — independiente de los demás cheques del
// mismo claimRecord. Desde 2026-09-26 (pedido del cliente) el PRIMER pago
// registrado de un cheque genera su deuda de operador ($OPERATOR_FEE, una
// por cheque: el id de la deuda es el checkId, así que pagos siguientes o el
// archivado posterior no la duplican).
// Permite sobrepago (a diferencia del modelo viejo, que lo rechazaba): si
// el P.A. manda de más, se registra igual, se marca totalmente pagado, y
// se guarda el excedente en el registro para poder avisar del caso raro.
export const markCheckPaid = async (checkId, paymentAmount, paymentDetails = {}) => {
  const checkRef = doc(db, CHECKS_COLLECTION, checkId);
  const debtRef = doc(db, OPERATOR_DEBTS_COLLECTION, checkId);
  return runTransaction(db, async transaction => {
    const checkDoc = await transaction.get(checkRef);
    if (!checkDoc.exists()) throw new Error('Check not found');
    const checkData = checkDoc.data();
    // Lectura antes de cualquier escritura (Firestore lo exige).
    const debtSnap = await transaction.get(debtRef);

    const currentPending = checkData.pending ?? checkData.totalCollected ?? 0;
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0 || checkData.paid) {
      throw new Error('Invalid payment amount or check already paid');
    }
    // Tolerancia de medio centavo: currentPending suele venir de una cadena
    // de multiplicaciones de porcentajes (amount * adjusterPercentage / 100
    // * csrPercentage / 100), así que un pago que "en plata" salda el cheque
    // completo puede dejar newPending en algo como 0.0000000003 en vez de 0
    // exacto — sin esto, ese residuo de punto flotante hacía que un pago
    // que sí cerraba el cheque se guardara como "parcial" con "Remaining
    // Balance: $0.00" (hallazgo real: invoice 156, Victor Torres).
    const newPending = currentPending - paymentAmount;
    const isFullyPaid = newPending <= 0.005;
    const overpaidBy = newPending < -0.005 ? Math.abs(newPending) : 0;

    const updates = {
      deposit: (checkData.deposit || 0) + paymentAmount,
      pending: isFullyPaid ? 0 : newPending,
      paid: isFullyPaid,
      updatedAt: Timestamp.now()
    };

    // Normalmente el invoice ya nació al generarlo (ensureCheckInvoiceNumber).
    // Si se pagó completo sin haberlo generado nunca, se reserva aquí, en la
    // misma transacción: si el pago no se confirma, el número no se gasta.
    let invoiceNumber = checkData.invoiceNumber || '';
    if (isFullyPaid && !invoiceNumber) {
      invoiceNumber = await nextInvoiceNumberIn(transaction);
      updates.invoiceNumber = invoiceNumber;
    }

    const paymentRecord = {
      amount: paymentAmount,
      reference: paymentDetails.reference || '',
      note: paymentDetails.note || '',
      // Día en que el dinero llegó (BofA / Zelle), YYYY-MM-DD. Puede ser anterior al registro.
      receivedDate: paymentDetails.receivedDate || '',
      overpaidBy,
      recordedAt: Timestamp.now(),
      recordedBy: getCurrentUserEmail()
    };
    if (paymentDetails.backup) {
      paymentRecord.backup = {
        storageKey: paymentDetails.backup.storageKey,
        name: paymentDetails.backup.name,
        mimeType: paymentDetails.backup.mimeType,
        size: paymentDetails.backup.size,
        uploadedAt: paymentDetails.backup.uploadedAt || new Date().toISOString(),
        uploadedBy: paymentDetails.backup.uploadedBy || getCurrentUserEmail()
      };
    }
    updates.paymentHistory = [...(checkData.paymentHistory || []), paymentRecord];

    transaction.update(checkRef, updates);
    if (debtSnap.exists() && updates.invoiceNumber) {
      transaction.update(debtRef, { invoiceNumber: updates.invoiceNumber });
    }
    if (!debtSnap.exists()) {
      transaction.set(debtRef, {
        invoiceNumber,
        claimId: checkId,
        clientName: checkData.clientName || 'Unknown',
        checkDate: checkData.checkDate || null,
        amount: OPERATOR_FEE,
        paid: false,
        createdAt: Timestamp.now(),
        createdBy: getCurrentUserEmail(),
        companyName: checkData.companyName || 'Unknown',
        adjusterName: checkData.adjusterName || 'Unknown'
      });
    }
    appendActivity(transaction, {
      event: isFullyPaid ? 'payment_full' : 'payment_partial', targetId: checkId,
      reference: `Claim ${checkData.claimNumber || ''}${invoiceNumber ? ` · Invoice ${invoiceNumber}` : ''}`,
      amount: paymentAmount
    });

    return { isFullyPaid, overpaidBy, remaining: isFullyPaid ? 0 : newPending, totalPaid: (checkData.deposit || 0) + paymentAmount, checkSnapshot: { ...checkData, ...updates, id: checkId } };
  });
};

// Paso 6 (ver claim_lifecycle_restructure.md): archivar es lo que genera la
// deuda de operador ahora — no el pago. Manual, por cheque, exige que ya
// esté pagado (no tiene sentido archivar algo sin cobrar).
//
// Corre en una sola transacción (antes eran dos escrituras separadas: si
// fallaba justo entre marcar archivado y crear la deuda, el cheque quedaba
// archivado sin deuda y sin forma de reintentar desde la UI, que oculta
// "Continuar" en cuanto ve archived=true). La deuda usa el propio checkId
// como ID de documento (en vez de addDoc + query de deduplicación por
// invoiceNumber) para que un reintento — o una doble ejecución por
// concurrencia — sea naturalmente idempotente: transaction.get(debtRef) ya
// resuelve la deduplicación sin necesitar una query dentro de la
// transacción, que Firestore no soporta.
export const archiveCheck = async checkId => {
  const checkRef = doc(db, CHECKS_COLLECTION, checkId);
  const debtRef = doc(db, OPERATOR_DEBTS_COLLECTION, checkId);
  return runTransaction(db, async transaction => {
    const checkSnap = await transaction.get(checkRef);
    if (!checkSnap.exists()) throw new Error('Check not found');
    const check = checkSnap.data();
    if (!check.paid) throw new Error('CHECK_NOT_PAID');

    // Todas las lecturas de la transacción van antes que cualquier escritura
    // (Firestore lo exige) — por eso este get() se hace acá aunque solo se
    // use en una de las dos ramas de abajo.
    const debtSnap = await transaction.get(debtRef);

    if (check.archived) {
      return debtSnap.exists() ? { id: debtRef.id, ...debtSnap.data() } : null;
    }

    transaction.update(checkRef, {
      archived: true,
      archivedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    if (debtSnap.exists()) return { id: debtRef.id, ...debtSnap.data() };

    const debtData = {
      invoiceNumber: check.invoiceNumber,
      claimId: checkId,
      clientName: check.clientName || 'Unknown',
      checkDate: check.checkDate || null,
      amount: OPERATOR_FEE,
      paid: false,
      createdAt: Timestamp.now(),
      createdBy: getCurrentUserEmail(),
      companyName: check.companyName || 'Unknown',
      adjusterName: check.adjusterName || 'Unknown'
    };
    transaction.set(debtRef, debtData);
    return { id: debtRef.id, ...debtData };
  });
};

export const getCheckById = async checkId => {
  const snapshot = await getDoc(doc(db, CHECKS_COLLECTION, checkId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

export const getAllChecks = async () => {
  const snapshot = await getDocs(collection(db, CHECKS_COLLECTION));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
};

// El modelo viejo `claims` guardaba `crmLink` ya armado; los `checks`
// nuevos solo guardan `crmClaimId` (copiado del claimRecord al firmarse,
// ver createClaimFromStatement), así que se arma acá con la misma URL de
// siempre — necesario para ClaimsDirectory.jsx (portal de ajustadores),
// que antes leía la colección vieja y esperaba este campo ya listo.
const withCrmLink = (check) => ({
  ...check,
  crmLink: check.crmClaimId ? `https://app.organizedadjusting.com/app/claims/${check.crmClaimId}` : ''
});

// Reemplaza a getAllClaims/getClaimsByCompany para ClaimsDirectory.jsx: los
// checks ya nacen con todos los campos de exhibición (clientName,
// companyName, adjusterName, claimNumber, cause, checkDate...) copiados
// desde su claimRecord al firmarse — no hace falta unir colecciones.
export const getAllChecksForDirectory = async () => {
  const snapshot = await getDocs(collection(db, CHECKS_COLLECTION));
  return snapshot.docs.map(item => withCrmLink({ id: item.id, ...item.data() }));
};

// Filtrado por companyName vía query (no en memoria): firestore.rules exige
// que la consulta traiga where('companyName', '==', ...) para que un
// ajustador pueda leer la lista completa — mismo motivo que
// getClaimsByCompany en el modelo viejo (ver comentario en ClaimsDirectory.jsx).
export const getChecksForDirectoryByCompany = async (companyName) => {
  const snapshot = await getDocs(query(collection(db, CHECKS_COLLECTION), where('companyName', '==', companyName)));
  return snapshot.docs.map(item => withCrmLink({ id: item.id, ...item.data() }));
};

// Para un ajustador SIN permiso de ver toda la empresa (viewAllClaims
// false): firestore.rules exige where('adjusterName', '==', ...) además de
// where('companyName', '==', ...), o rechaza el query completo — ver
// adjusterCanViewAll()/adjusterOwnName() en firestore.rules.
// También trae los cheques de reclamos donde tiene acceso sin ser el PA
// principal (accessNames, ver utils/claimAccess.js).
export const getChecksForDirectoryByCompanyAndAdjuster = async (companyName, adjusterName) => {
  const base = collection(db, CHECKS_COLLECTION);
  const [own, shared] = await Promise.all([
    getDocs(query(base, where('companyName', '==', companyName), where('adjusterName', '==', adjusterName))),
    getDocs(query(base, where('companyName', '==', companyName), where('accessNames', 'array-contains', adjusterName)))
  ]);
  return mergeById([...own.docs, ...shared.docs].map(item => withCrmLink({ id: item.id, ...item.data() })));
};

// Un solo cheque para la página de detalle completo (/claim-status/claim/:id).
// Es un get() por doc id, no una query de lista — la regla "read" de
// firestore.rules para checks/{checkId} se evalúa igual contra resource.data,
// así que no necesita el where('companyName', ...) que sí exigen las
// consultas de lista (ver getChecksForDirectoryByCompany más arriba).
export const getCheckForDirectoryById = async (checkId) => {
  const snapshot = await getDoc(doc(db, CHECKS_COLLECTION, checkId));
  return snapshot.exists() ? withCrmLink({ id: snapshot.id, ...snapshot.data() }) : null;
};

// "My Claims" nuevo (ver claim_lifecycle_restructure.md): un claimRecord con
// sus checks anidados, para las cards desplegables (una card = un reclamo,
// adentro la lista de sus cheques). Lee ambas colecciones y las une en
// memoria — con los volúmenes actuales (~100 claimRecords) es más simple
// que mantener una desnormalización, y evita otra ida y vuelta por cheque.
export const getAllClaimRecordsWithChecks = async () => {
  const [claimRecordsSnap, checksSnap] = await Promise.all([
    getDocs(collection(db, 'claimRecords')),
    getDocs(collection(db, CHECKS_COLLECTION))
  ]);
  const checksByParent = {};
  checksSnap.docs.forEach(item => {
    const check = { id: item.id, ...item.data() };
    const parentId = check.claimRecordId || '__orphan__';
    (checksByParent[parentId] = checksByParent[parentId] || []).push(check);
  });
  return claimRecordsSnap.docs.map(item => ({
    id: item.id,
    ...item.data(),
    checks: checksByParent[item.id] || []
  }));
};

export const getChecksByClaimRecordId = async claimRecordId => {
  const snapshot = await getDocs(query(collection(db, CHECKS_COLLECTION), where('claimRecordId', '==', claimRecordId)));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
};

// Cheques de un reclamo para el PA: las reglas solo le dejan leer cheques de
// su empresa a los que tenga acceso, así que la consulta tiene que decirlo
// (Firestore rechaza una consulta que podría devolver documentos no
// permitidos). viewer = { name, canViewAll } del usuario logueado.
export const getChecksForClaimRecord = async (claimRecord, isAdmin, viewer = {}) => {
  if (isAdmin) return getChecksByClaimRecordId(claimRecord.id);
  // CSR Assistant: lee el espejo sin datos de CSR, no `checks` (firestore.rules lo impide).
  if (viewer.summaryOnly) {
    const snapshot = await getDocs(query(collection(db, CHECK_SUMMARIES_COLLECTION),
      where('claimRecordId', '==', claimRecord.id), where('companyName', '==', claimRecord.companyName || '')));
    return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
  }
  const base = [collection(db, CHECKS_COLLECTION),
    where('claimRecordId', '==', claimRecord.id),
    where('companyName', '==', claimRecord.companyName || '')];
  const toList = snapshot => snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
  if (viewer.canViewAll) return toList(await getDocs(query(...base)));
  const name = viewer.name || claimRecord.adjusterName || '';
  const [own, shared] = await Promise.all([
    getDocs(query(...base, where('adjusterName', '==', name))),
    getDocs(query(...base, where('accessNames', 'array-contains', name)))
  ]);
  return mergeById([...toList(own), ...toList(shared)]);
};

// Borrar un cheque en cascada con su deuda de operador — mismo patrón que
// el deleteClaim viejo: si el cheque ya estaba archivado, seguramente
// generó una deuda real (ver archiveCheck) que quedaría huérfana en el
// panel de Deuda de Operador si no se borra junto con él.
export const deleteCheck = async checkId => {
  const checkSnap = await getDoc(doc(db, CHECKS_COLLECTION, checkId));
  const invoiceNumber = checkSnap.exists() ? checkSnap.data().invoiceNumber : null;
  await deleteDoc(doc(db, CHECKS_COLLECTION, checkId));
  await deleteDoc(doc(db, CHECK_SUMMARIES_COLLECTION, checkId)).catch(() => {});
  // La deuda del modelo nuevo usa el checkId como id; la vieja se busca por invoice.
  const ownDebt = await getDoc(doc(db, OPERATOR_DEBTS_COLLECTION, checkId)).catch(() => null);
  if (ownDebt?.exists()) {
    await deleteOperatorDebt(checkId, 'check_deleted').catch(err => console.warn('Error cascading operator debt deletion (non-critical):', err));
  } else if (invoiceNumber) {
    try {
      const debt = await getOperatorDebtByInvoice(invoiceNumber);
      if (debt) await deleteOperatorDebt(debt.id, 'check_deleted');
    } catch (cascadeError) {
      console.warn('Error cascading operator debt deletion (non-critical):', cascadeError);
    }
  }
};

// Borrar un claimRecord en cascada con TODOS sus cheques (y la deuda de
// cada uno que corresponda) — un claimRecord sin dueño de sus checks los
// dejaría huérfanos, apuntando a un padre que ya no existe.
export const deleteClaimRecordCascade = async claimRecordId => {
  const checks = await getChecksByClaimRecordId(claimRecordId);
  for (const check of checks) {
    await deleteCheck(check.id);
  }
  await deleteDoc(doc(db, 'claimRecords', claimRecordId));
  return { deletedChecks: checks.length };
};

// Paso 3 (ver claim_lifecycle_restructure.md): el cheque llega con los
// valores REALES, que mandan sobre el aproximado del Closing Statement.
// Recalcula los montos derivados igual que createClaim/updateClaim.
//
// Corre en transacción y recalcula `pending` restando lo que YA se haya
// depositado (igual que updateClaim en el modelo viejo) — antes esto
// reescribía siempre pending = totalCollected completo, así que editar un
// cheque ya parcial o totalmente pagado revertía el saldo a como si nada
// se hubiera cobrado. Un cheque archivado (ya cerrado, con deuda de
// operador generada) no debería tener sus montos financieros editados acá
// — se rechaza explícitamente en vez de dejar la deuda ya creada
// desincronizada del monto real.
export const updateCheckRealValues = async (checkId, data) => {
  const checkRef = doc(db, CHECKS_COLLECTION, checkId);
  return runTransaction(db, async transaction => {
    const checkSnap = await transaction.get(checkRef);
    if (!checkSnap.exists()) throw new Error('Check not found');
    const current = checkSnap.data();
    if (current.archived) throw new Error('CHECK_ALREADY_ARCHIVED');

    const amount = Number(data.amount) || 0;
    const adjusterPercentage = Number(data.adjusterPercentage) || 0;
    const csrPercentage = Number(data.csrPercentage) || 0;
    const adjusterCollected = (amount * adjusterPercentage) / 100;
    const csrCollected = (adjusterCollected * csrPercentage) / 100;
    const totalCollected = csrCollected;
    const deposit = current.deposit || 0;
    const pending = current.paid ? 0 : Math.max(0, totalCollected - deposit);

    const payload = {
      checkDate: data.checkDate || '',
      payableTo: data.payableTo || '',
      amount,
      adjusterPercentage,
      adjusterCollected,
      csrPercentage,
      csrCollected,
      totalCollected,
      pending,
      realValuesConfirmed: true,
      // Se movió del Paso 1 al Paso 3 — los porcentajes reales recién se
      // conocen acá, no tenía sentido confirmarlos antes de tenerlos.
      paPercentageConfirmed: Boolean(data.paPercentageConfirmed),
      updatedAt: Timestamp.now()
    };
    transaction.update(checkRef, payload);
    return payload;
  });
};

// Backup secuencial del Paso 3 — PRIMERO Hostinger (archivo real), LUEGO
// confirmación manual de CRM (ver Fase 1 en el plan: ambos obligatorios,
// en ese orden, para cerrar el paso).
export const uploadCheckBackup = async (checkId, file) => {
  const backup = await uploadBackup({ file, claimId: checkId, invoiceNumber: '', type: 'check' });
  const payload = {
    'checkAttachment.storageKey': backup.storageKey,
    'checkAttachment.name': backup.name,
    'checkAttachment.mimeType': backup.mimeType,
    'checkAttachment.size': backup.size,
    'checkAttachment.uploadedAt': Timestamp.now(),
    'checkAttachment.uploadedBy': backup.uploadedBy || getCurrentUserEmail(),
    updatedAt: Timestamp.now()
  };
  await updateDoc(doc(db, CHECKS_COLLECTION, checkId), payload);
  return payload;
};

// Comprobante (BofA / Zelle) como RESPALDO de un pago que ya se cobró: lo adjunta
// al pago existente que aún no tiene comprobante o, si no hay, crea una entrada
// histórica. NO toca deposit / pending / paid ni crea la deuda del operador
// (eso solo lo hace markCheckPaid, cuando el pago es nuevo).
export const attachCheckPaymentBackup = async (checkId, file, { reference = '', receivedDate = '' } = {}) => {
  const backup = await uploadBackup({ file, claimId: checkId, invoiceNumber: '', type: 'payment' });
  const checkRef = doc(db, CHECKS_COLLECTION, checkId);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(checkRef);
    if (!snap.exists()) throw new Error('Check not found');
    const data = snap.data();
    const backupData = {
      storageKey: backup.storageKey, name: backup.name, mimeType: backup.mimeType, size: backup.size,
      uploadedAt: backup.uploadedAt || new Date().toISOString(), uploadedBy: backup.uploadedBy || getCurrentUserEmail()
    };
    const history = [...(data.paymentHistory || [])];
    const index = history.findIndex(payment => !payment?.backup?.storageKey);
    if (index >= 0) {
      history[index] = { ...history[index], reference: history[index].reference || reference, receivedDate: receivedDate || history[index].receivedDate || '', backup: backupData };
    } else {
      history.push({
        amount: data.deposit || data.totalCollected || 0,
        reference: reference || 'Pago registrado anteriormente',
        receivedDate,
        note: 'Comprobante agregado como respaldo (sin pago nuevo).',
        backupOnly: true,
        recordedAt: Timestamp.now(),
        recordedBy: getCurrentUserEmail(),
        backup: backupData
      });
    }
    transaction.update(checkRef, { paymentHistory: history, updatedAt: Timestamp.now() });
    return history;
  });
};

// Corrige un pago ya registrado: monto, fecha de pago y/o referencia (p. ej. se
// anotó $299.15 en vez de $299.19). El monto recalcula lo cobrado (deposit), el
// saldo (pending) y si el cheque queda pagado; si con la corrección queda
// saldado y no tenía invoice, se le reserva el número como en markCheckPaid. La
// deuda del operador no cambia (nace con el primer pago). No se permite bajar
// un cheque ya cobrado por debajo de lo pactado (ya pasó a "Comisión cobrada").
export const updateCheckPayment = async (checkId, paymentIndex, changes = {}) => {
  const checkRef = doc(db, CHECKS_COLLECTION, checkId);
  const debtRef = doc(db, OPERATOR_DEBTS_COLLECTION, checkId);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(checkRef);
    if (!snap.exists()) throw new Error('Check not found');
    const data = snap.data();
    const debtSnap = await transaction.get(debtRef);
    const history = [...(data.paymentHistory || [])];
    const entry = history[paymentIndex];
    if (!entry) throw new Error('Payment not found');

    const next = { ...entry, editedAt: Timestamp.now(), editedBy: getCurrentUserEmail() };
    if (changes.receivedDate !== undefined) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(changes.receivedDate || '')) throw new Error('INVALID_DATE');
      next.receivedDate = changes.receivedDate;
    }
    if (changes.reference !== undefined) next.reference = String(changes.reference || '').trim();

    const updates = { updatedAt: Timestamp.now() };
    let becamePaid = false;
    let invoiceNumber = data.invoiceNumber || '';
    const oldAmount = Number(entry.amount) || 0;
    const newAmount = changes.amount === undefined ? oldAmount : Math.round(Number(changes.amount) * 100) / 100;
    if (newAmount !== oldAmount) {
      if (entry.backupOnly) throw new Error('BACKUP_ONLY_AMOUNT');
      if (!Number.isFinite(newAmount) || newAmount <= 0) throw new Error('INVALID_AMOUNT');
      const delta = newAmount - oldAmount;
      const currentPending = data.paid ? 0 : (data.pending ?? data.totalCollected ?? 0);
      const newPending = currentPending - delta;
      const isFullyPaid = newPending <= 0.005;
      if (data.paid && !isFullyPaid) throw new Error('PAYMENT_WOULD_UNPAY');
      next.amount = newAmount;
      next.previousAmount = entry.previousAmount ?? oldAmount;
      next.overpaidBy = newPending < -0.005 ? Math.abs(newPending) : 0;
      updates.deposit = Math.round(((Number(data.deposit) || 0) + delta) * 100) / 100;
      updates.pending = isFullyPaid ? 0 : Math.round(newPending * 100) / 100;
      updates.paid = isFullyPaid;
      becamePaid = isFullyPaid && !data.paid;
      if (becamePaid && !invoiceNumber) {
        invoiceNumber = await nextInvoiceNumberIn(transaction);
        updates.invoiceNumber = invoiceNumber;
      }
    }
    history[paymentIndex] = next;
    updates.paymentHistory = history;
    transaction.update(checkRef, updates);
    if (updates.invoiceNumber && debtSnap.exists()) transaction.update(debtRef, { invoiceNumber: updates.invoiceNumber });
    appendActivity(transaction, {
      event: 'payment_edited', targetId: checkId,
      reference: `Claim ${data.claimNumber || ''}${invoiceNumber ? ` · Invoice ${invoiceNumber}` : ''}`,
      amount: newAmount
    });
    return {
      becamePaid,
      remaining: updates.pending ?? data.pending ?? 0,
      totalPaid: updates.deposit ?? data.deposit ?? 0,
      checkSnapshot: { ...data, ...updates, id: checkId }
    };
  });
};

// Actualizar un reclamo
export const updateClaim = async (claimId, claimData) => {
  try {
    const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
    const currentClaimDoc = await getDoc(claimRef);
    if (!currentClaimDoc.exists()) {
      throw new Error('Claim not found');
    }
    const currentClaim = currentClaimDoc.data();

    const adjusterCollected = (claimData.amount * claimData.adjusterPercentage) / 100;
    const csrCollected = (adjusterCollected * claimData.csrPercentage) / 100;
    const totalCollected = csrCollected;

    const crmLink = `https://app.organizedadjusting.com/app/claims/${claimData.crmClaimId || ''}`;

    const updatedNote = generateNoteForCRM(
      { ...claimData, crmLink: crmLink },
      claimData.invoiceNumber
    );

    // El pendiente debe restar lo que ya se depositó — antes se
    // reescribía siempre como el totalCollected completo al editar,
    // borrando de un plumazo cualquier pago parcial ya registrado.
    const deposit = currentClaim.deposit || 0;
    const pending = currentClaim.paid ? 0 : Math.max(0, totalCollected - deposit);

    const updatedClaim = {
      invoiceNumber: claimData.invoiceNumber,
      checkDate: claimData.checkDate,
      companyName: claimData.companyName,
      payableTo: claimData.payableTo,
      clientName: claimData.clientName,
      cause: claimData.cause,
      adjusterName: claimData.adjusterName,
      claimNumber: claimData.claimNumber,
      crmClaimId: claimData.crmClaimId || '',
      crmLink: crmLink,
      amount: claimData.amount,
      adjusterPercentage: claimData.adjusterPercentage,
      adjusterCollected: adjusterCollected,
      csrPercentage: claimData.csrPercentage,
      csrCollected: csrCollected,
      totalCollected: totalCollected,
      status: claimData.status,
      pending,
      paid: currentClaim.paid || false,
      generatedNote: updatedNote,
      updatedAt: Timestamp.now()
    };

    const batch = writeBatch(db);
    batch.update(claimRef, updatedClaim);
    appendActivity(batch, { event: 'claim_updated', targetId: claimId, reference: `Claim ${updatedClaim.claimNumber || ''} · Invoice ${updatedClaim.invoiceNumber || ''}` });
    await batch.commit();

    return { id: claimId, ...updatedClaim };
  } catch (error) {
    console.error('Error updating claim:', error);
    throw error;
  }
};

// Eliminar un reclamo
export const deleteClaim = async (claimId) => {
  try {
    const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
    const claimSnap = await getDoc(claimRef);
    const invoiceNumber = claimSnap.exists() ? claimSnap.data().invoiceNumber : null;

    await deleteDoc(claimRef);

    // Cascada: la deuda del operador asociada a este invoice pierde
    // sentido si el reclamo ya no existe — antes quedaba huérfana en el
    // panel de Deuda del Operador, contándose en las estadísticas sin
    // ningún reclamo real detrás.
    if (invoiceNumber) {
      try {
        const debt = await getOperatorDebtByInvoice(invoiceNumber);
        if (debt) {
          await deleteOperatorDebt(debt.id, 'claim_deleted');
        }
      } catch (cascadeError) {
        console.warn('Error cascading operator debt deletion (non-critical):', cascadeError);
      }
    }

    return true;
  } catch (error) {
    console.error('Error deleting claim:', error);
    throw error;
  }
};

// Obtener todos los reclamos
// `strict: true` lanza el error en vez de devolver []: las herramientas que
// borran o crean deudas no deben confundir "no pude leer" con "no hay nada".
export const getAllClaims = async ({ strict = false } = {}) => {
  try {
    const q = query(collection(db, CLAIMS_COLLECTION), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting claims:', error);
    if (strict) throw error;
    return [];
  }
};

// Obtener un reclamo por ID
export const getClaimById = async (claimId) => {
  try {
    const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
    const claimDoc = await getDoc(claimRef);
    
    if (claimDoc.exists()) {
      return { id: claimDoc.id, ...claimDoc.data() };
    } else {
      throw new Error('Claim not found');
    }
  } catch (error) {
    console.error('Error getting claim by ID:', error);
    throw error;
  }
};

// Obtener reclamos por empresa (usado por el rol 'adjuster', limitado a su empresa)
export const getClaimsByCompany = async (companyName) => {
  const q = query(collection(db, CLAIMS_COLLECTION), where('companyName', '==', companyName));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Obtener reclamos por ajustador
export const getClaimsByAdjuster = async (adjusterName) => {
  try {
    const q = query(collection(db, CLAIMS_COLLECTION), where('adjusterName', '==', adjusterName));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting claims by adjuster:', error);
    return [];
  }
};

// Actualizar estado de adjunto
// El backup del cheque vive en el CRM externo (Organized Adjusting), no en
// esta app — antes se guardaba el string literal "uploaded" en
// checkAttachment.url simulando ser una URL real, lo cual no servía para
// nada y podía confundirse con un link válido. Solo se registra que se
// subió y cuándo.
export const updateAttachmentStatus = async (claimId, backup = null) => {
  try {
    const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
    const updates = {
      attachmentsUploaded: true,
      'checkAttachment.uploadedAt': Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    if (backup) {
      updates['checkAttachment.storageKey'] = backup.storageKey;
      updates['checkAttachment.name'] = backup.name;
      updates['checkAttachment.mimeType'] = backup.mimeType;
      updates['checkAttachment.size'] = backup.size;
      updates['checkAttachment.uploadedBy'] = backup.uploadedBy || getCurrentUserEmail();
    }
    await updateDoc(claimRef, updates);
  } catch (error) {
    console.error('Error updating attachment status:', error);
    throw error;
  }
};

// Marcar como pagado. Corre dentro de una transacción de Firestore para
// que un doble clic o dos pestañas registrando el mismo pago a la vez no
// puedan aplicar el depósito dos veces (antes era un simple read-then-write).
export const markAsPaid = async (claimId, paymentAmount, paymentDetails = {}) => {
  const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
  try {
    const result = await runTransaction(db, async (transaction) => {
      const claimDoc = await transaction.get(claimRef);
      if (!claimDoc.exists()) {
        throw new Error('Claim not found');
      }
      const claimData = claimDoc.data();

      const currentPending = claimData.pending ?? claimData.totalCollected ?? 0;
      if (!Number.isFinite(paymentAmount) || paymentAmount <= 0 || paymentAmount > currentPending + 0.005 || claimData.paid) {
        throw new Error('Invalid payment amount or claim already paid');
      }
      // Misma tolerancia de medio centavo que markCheckPaid — ver ese
      // comentario para el bug real que esto evita (residuo de punto
      // flotante dejando un pago que sí saldaba el cheque marcado como
      // "parcial").
      const newPending = currentPending - paymentAmount;
      const isFullyPaid = newPending <= 0.005;

      const updates = {
        deposit: (claimData.deposit || 0) + paymentAmount,
        pending: isFullyPaid ? 0 : newPending,
        paid: isFullyPaid,
        updatedAt: Timestamp.now()
      };

      const paymentRecord = {
        amount: paymentAmount,
        reference: paymentDetails.reference || '',
        note: paymentDetails.note || '',
        recordedAt: Timestamp.now(),
        recordedBy: getCurrentUserEmail()
      };
      if (paymentDetails.backup) {
        paymentRecord.backup = {
          storageKey: paymentDetails.backup.storageKey,
          name: paymentDetails.backup.name,
          mimeType: paymentDetails.backup.mimeType,
          size: paymentDetails.backup.size,
          uploadedAt: paymentDetails.backup.uploadedAt || new Date().toISOString(),
          uploadedBy: paymentDetails.backup.uploadedBy || getCurrentUserEmail()
        };
      }
      updates.paymentHistory = [...(claimData.paymentHistory || []), paymentRecord];

      if (isFullyPaid && claimData.status !== 'completed') {
        updates.status = 'completed';
      }
      if (isFullyPaid) {
        // Usado por el Dashboard para calcular el tiempo real de cobro
        // (antes se medía contra createdAt, que no refleja cuándo se
        // cobró de verdad).
        updates.paymentDate = new Date().toISOString();
      }

      transaction.update(claimRef, updates);
      appendActivity(transaction, {
        event: isFullyPaid ? 'payment_full' : 'payment_partial', targetId: claimId,
        reference: `Claim ${claimData.claimNumber || ''} · Invoice ${claimData.invoiceNumber || ''}`,
        amount: paymentAmount
      });

      return {
        isFullyPaid,
        remaining: isFullyPaid ? 0 : newPending,
        totalPaid: (claimData.deposit || 0) + paymentAmount,
        claimSnapshot: { ...claimData, ...updates, id: claimId }
      };
    });

    // El claim se convierte en invoice (y genera deuda de operador) recién
    // acá, cuando queda totalmente pagado por el ajustador — no al crearlo.
    // createOperatorDebtForClaim ya dedupe por invoiceNumber, así que un
    // pago parcial que no llega a isFullyPaid no crea nada, y no hay riesgo
    // de duplicar la deuda si markAsPaid se llama de nuevo después.
    if (result.isFullyPaid) {
      await createOperatorDebtForClaim(result.claimSnapshot);
    }

    return result;
  } catch (error) {
    console.error('Error marking as paid:', error);
    throw error;
  }
};

// Adjunta un comprobante después de haber registrado el pago. No toca
// deposit/pending/paid: sirve para completar el expediente sin volver a
// cobrar ni alterar la contabilidad. Si paymentIndex es null, crea una
// entrada histórica para reclamos pagados antes de existir paymentHistory.
export const attachPaymentBackup = async (claimId, paymentIndex, backup) => {
  const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
  return runTransaction(db, async (transaction) => {
    const claimDoc = await transaction.get(claimRef);
    if (!claimDoc.exists()) throw new Error('Claim not found');
    const claimData = claimDoc.data();
    const history = [...(claimData.paymentHistory || [])];
    const backupData = {
      storageKey: backup.storageKey,
      name: backup.name,
      mimeType: backup.mimeType,
      size: backup.size,
      uploadedAt: backup.uploadedAt || new Date().toISOString(),
      uploadedBy: backup.uploadedBy || getCurrentUserEmail()
    };

    if (paymentIndex == null) {
      history.push({
        amount: claimData.deposit || claimData.totalCollected || 0,
        reference: 'Pago registrado anteriormente',
        note: 'Comprobante agregado al expediente histórico.',
        recordedAt: Timestamp.now(),
        recordedBy: getCurrentUserEmail(),
        backup: backupData
      });
    } else {
      if (!Number.isInteger(paymentIndex) || !history[paymentIndex]) throw new Error('Payment record not found');
      history[paymentIndex] = { ...history[paymentIndex], backup: backupData };
    }

    transaction.update(claimRef, { paymentHistory: history, updatedAt: Timestamp.now() });
    return history;
  });
};

// Actualizar estado del reclamo
export const updateClaimStatus = async (claimId, newStatus) => {
  try {
    const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
    await runTransaction(db, async transaction => {
      const snapshot = await transaction.get(claimRef);
      if (!snapshot.exists()) throw new Error('Claim not found');
      if (snapshot.data().status === newStatus) return;
      transaction.update(claimRef, { status: newStatus, updatedAt: Timestamp.now() });
      appendActivity(transaction, { event: 'claim_updated', targetId: claimId, reference: snapshot.data().claimNumber || claimId, status: newStatus });
    });
    return true;
  } catch (error) {
    console.error('Error updating claim status:', error);
    throw error;
  }
};

// Obtener reclamos por estado
export const getClaimsByStatus = async (status) => {
  try {
    const q = query(collection(db, CLAIMS_COLLECTION), where('status', '==', status));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting claims by status:', error);
    return [];
  }
};

// Obtener reclamos pagados vs no pagados
export const getClaimsByPaymentStatus = async (paid) => {
  try {
    const q = query(collection(db, CLAIMS_COLLECTION), where('paid', '==', paid));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting claims by payment status:', error);
    return [];
  }
};

// Obtener estadísticas
export const getStatistics = async () => {
  const [records, checks, debts] = await Promise.all([
    getDocs(collection(db, 'claimRecords')),
    getAllChecks(),
    getOperatorDebts()
  ]);
  return calculateAnalytics(records.docs.map(item => ({ ...item.data(), id: item.id })), checks, debts);
};

// Búsqueda avanzada
export const searchClaims = async (searchTerm) => {
  try {
    const allClaims = await getAllClaims();
    const term = searchTerm.toLowerCase();
    
    return allClaims.filter(claim => 
      claim.clientName?.toLowerCase().includes(term) ||
      claim.claimNumber?.toLowerCase().includes(term) ||
      claim.invoiceNumber?.toLowerCase().includes(term) ||
      claim.adjusterName?.toLowerCase().includes(term) ||
      claim.companyName?.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching claims:', error);
    return [];
  }
};

// ============ FUNCIONES PARA DEUDAS DEL OPERADOR ============

export const getOperatorDebts = async () => {
  try {
    const q = query(collection(db, OPERATOR_DEBTS_COLLECTION), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting operator debts:', error);
    throw error;
  }
};

export const getOperatorDebtByInvoice = async (invoiceNumber) => {
  try {
    const q = query(collection(db, OPERATOR_DEBTS_COLLECTION), where('invoiceNumber', '==', invoiceNumber));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const docRef = querySnapshot.docs[0];
      return { id: docRef.id, ...docRef.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting operator debt by invoice:', error);
    throw error;
  }
};

export const updateOperatorDebtPayment = async (debtId, paid, userEmail) => {
  try {
    const email = userEmail && userEmail !== 'undefined' ? userEmail : getCurrentUserEmail();
    
    const debtRef = doc(db, OPERATOR_DEBTS_COLLECTION, debtId);
    const debtDoc = await getDoc(debtRef);
    
    if (!debtDoc.exists()) {
      throw new Error('Debt not found');
    }
    
    const debtData = debtDoc.data();
    const currentPaidStatus = debtData.paid;
    
    if (currentPaidStatus === paid) {
      return { success: false, message: 'Debt already in this status' };
    }
    
    const action = paid ? 'marked_paid' : 'marked_unpaid';
    
    const updateData = {
      paid: paid,
      updatedAt: Timestamp.now(),
      updatedBy: email
    };
    
    if (paid) {
      updateData.paidAt = Timestamp.now();
      updateData.paidBy = email;
    } else {
      updateData.paidAt = null;
      updateData.paidBy = null;
    }
    
    await updateDoc(debtRef, updateData);
    
    try {
      await addDoc(collection(db, DEBT_PAYMENT_LOGS), {
        debtId: debtId,
        invoiceNumber: debtData.invoiceNumber || 'N/A',
        clientName: debtData.clientName || 'N/A',
        amount: debtData.amount || 0,
        action: action,
        previousStatus: currentPaidStatus,
        newStatus: paid,
        userEmail: email,
        timestamp: Timestamp.now()
      });
    } catch (logError) {
      console.warn('Error logging debt payment (non-critical):', logError);
    }
    
    return { 
      success: true, 
      message: paid ? 'Debt marked as paid' : 'Debt marked as unpaid',
      action: action
    };
  } catch (error) {
    console.error('Error updating operator debt:', error);
    throw error;
  }
};

export const createOperatorDebt = async (claim) => {
  try {
    const userEmail = getCurrentUserEmail();
    
    const existingDebt = await getOperatorDebtByInvoice(claim.invoiceNumber);
    if (existingDebt) {
      return existingDebt;
    }
    
    const docRef = await addDoc(collection(db, OPERATOR_DEBTS_COLLECTION), {
      invoiceNumber: claim.invoiceNumber,
      claimId: claim.id,
      clientName: claim.clientName || 'Unknown',
      checkDate: claim.checkDate || null,
      amount: OPERATOR_FEE,
      paid: false,
      createdAt: Timestamp.now(),
      createdBy: userEmail
    });
    
    return { id: docRef.id, invoiceNumber: claim.invoiceNumber, amount: OPERATOR_FEE };
  } catch (error) {
    console.error('Error creating operator debt:', error);
    throw error;
  }
};

export const regenerateMissingDebts = async () => {
  try {
    const claims = await getAllClaims({ strict: true });
    const existingDebts = await getOperatorDebts();
    const existingInvoiceNumbers = new Set(existingDebts.map(d => d.invoiceNumber));

    let createdCount = 0;

    for (const claim of claims) {
      // Solo genera deuda faltante para claims YA pagados por el ajustador
      // (invoices reales) — antes creaba deuda para cualquier claim
      // pendiente, contradiciendo la regla de que solo lo pagado cuenta.
      if (claim.paid && !existingInvoiceNumbers.has(claim.invoiceNumber)) {
        console.log(`Creating missing debt for invoice: ${claim.invoiceNumber}`);
        await createOperatorDebtForClaim(claim);
        createdCount++;
      }
    }

    return createdCount;
  } catch (error) {
    // Si no se pudo leer, NO se crea nada: devolver 0 aquí ocultaba el fallo.
    console.error('Error regenerating missing debts:', error);
    throw error;
  }
};

// Auditoría de deudas creadas prematuramente: antes, TODO claim creado
// generaba una deuda de operador de inmediato, sin esperar a que el
// ajustador pagara. Esto detecta esas deudas "huérfanas" (su claim
// asociado todavía no está pagado, o ya no existe) para poder revisarlas
// antes de decidir borrarlas.
export const auditOperatorDebts = async () => {
  try {
    // Deudas de dos modelos: las viejas (colección `claims`, por invoice) y
    // las nuevas (colección `checks`, la deuda usa el checkId como id). Una
    // deuda nueva es válida si su cheque ya tiene al menos un pago
    // registrado (regla desde 2026-09-26: la deuda nace con el primer pago).
    const [debts, claims, checks] = await Promise.all([getOperatorDebts(), getAllClaims({ strict: true }), getAllChecks()]);
    const claimsByInvoice = new Map(claims.map(c => [c.invoiceNumber, c]));
    const checksById = new Map(checks.map(c => [c.id, c]));
    const checksByInvoice = new Map(checks.filter(c => c.invoiceNumber).map(c => [c.invoiceNumber, c]));
    const hasPayment = record => Boolean(record.paid) || Number(record.deposit) > 0 || (record.paymentHistory || []).length > 0;

    const premature = [];
    const valid = [];
    const orphaned = [];

    for (const debt of debts) {
      // Facturas manuales y de contratos no tienen cheque: siempre válidas.
      if (isManualDebt(debt)) { valid.push(debt); continue; }
      const check = checksById.get(debt.id) || checksById.get(debt.claimId) || checksByInvoice.get(debt.invoiceNumber);
      if (check) {
        if (hasPayment(check)) valid.push(debt); else premature.push(debt);
        continue;
      }
      const claim = claimsByInvoice.get(debt.invoiceNumber);
      if (!claim) {
        orphaned.push(debt);
      } else if (!claim.paid) {
        premature.push(debt);
      } else {
        valid.push(debt);
      }
    }

    return {
      total: debts.length,
      validCount: valid.length,
      prematureCount: premature.length,
      orphanedCount: orphaned.length,
      premature,
      orphaned
    };
  } catch (error) {
    // Un fallo de lectura no es "todo válido": se propaga para que nadie
    // limpie deudas con datos incompletos.
    console.error('Error auditing operator debts:', error);
    throw error;
  }
};

// Ajusta al valor vigente (OPERATOR_FEE) las deudas PENDIENTES creadas con
// otra tarifa. Las pagadas no se tocan. Deja registro en debtPaymentLogs.
export const updatePendingDebtsToCurrentFee = async () => {
  const debts = await getOperatorDebts();
  const targets = debts.filter(debt => !debt.paid && !isManualDebt(debt) && Number(debt.amount) !== OPERATOR_FEE);
  for (let start = 0; start < targets.length; start += 200) {
    const batch = writeBatch(db);
    targets.slice(start, start + 200).forEach(debt => {
      batch.update(doc(db, OPERATOR_DEBTS_COLLECTION, debt.id), { amount: OPERATOR_FEE, previousAmount: Number(debt.amount) || 0, updatedAt: Timestamp.now() });
      batch.set(doc(collection(db, 'debtPaymentLogs')), {
        debtId: debt.id, invoiceNumber: debt.invoiceNumber || '', action: 'fee_update',
        from: Number(debt.amount) || 0, to: OPERATOR_FEE, by: getCurrentUserEmail(), at: Timestamp.now()
      });
    });
    await batch.commit();
  }
  return { updated: targets.length };
};

// Borra las deudas de operador "prematuras" detectadas por
// auditOperatorDebts (su claim aún no está pagado por el ajustador, o ya
// no existe). Usa deleteOperatorDebt para que cada borrado quede
// registrado en debtPaymentLogs con motivo 'premature_cleanup'.
export const cleanupPrematureOperatorDebts = async () => {
  const audit = await auditOperatorDebts();
  const toDelete = [...audit.premature, ...audit.orphaned];

  let deletedCount = 0;
  const failedIds = [];
  for (const debt of toDelete) {
    try {
      await deleteOperatorDebt(debt.id, 'premature_cleanup');
      deletedCount++;
    } catch (error) {
      console.error(`Error cleaning up debt ${debt.id}:`, error);
      failedIds.push(debt.id);
    }
  }

  return { deletedCount, failedIds, ...audit };
};

export const getOperatorDebtSummary = async () => {
  try {
    const debts = await getOperatorDebts();
    const totalPending = debts.filter(d => !d.paid).reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalPaid = debts.filter(d => d.paid).reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalAll = totalPending + totalPaid;
    
    return {
      totalPending,
      totalPaid,
      totalAll,
      pendingCount: debts.filter(d => !d.paid).length,
      paidCount: debts.filter(d => d.paid).length,
      totalCount: debts.length
    };
  } catch (error) {
    // Antes devolvía todo en 0 y la pantalla mostraba "deuda $0" ante un fallo.
    console.error('Error getting operator debt summary:', error);
    throw error;
  }
};

export const getDebtPaymentLogs = async (debtId = null) => {
  try {
    let q;
    if (debtId) {
      q = query(
        collection(db, DEBT_PAYMENT_LOGS),
        where('debtId', '==', debtId),
        orderBy('timestamp', 'desc')
      );
    } else {
      q = query(collection(db, DEBT_PAYMENT_LOGS), orderBy('timestamp', 'desc'));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting debt payment logs:', error);
    return [];
  }
};

export const deleteOperatorDebt = async (debtId, reason = 'manual') => {
  try {
    const debtRef = doc(db, OPERATOR_DEBTS_COLLECTION, debtId);
    const debtDoc = await getDoc(debtRef);
    if (!debtDoc.exists()) {
      throw new Error('Debt not found');
    }
    const debtData = debtDoc.data();
    const userEmail = getCurrentUserEmail();

    await deleteDoc(debtRef);

    // Antes borrar una deuda no dejaba ningún rastro en debtPaymentLogs
    // (a diferencia de marcarla pagada) — se podía borrar el historial de
    // auditoría de una deuda ya pagada sin dejar evidencia de quién lo hizo.
    try {
      await addDoc(collection(db, DEBT_PAYMENT_LOGS), {
        debtId,
        invoiceNumber: debtData.invoiceNumber || 'N/A',
        clientName: debtData.clientName || 'N/A',
        amount: debtData.amount || 0,
        action: 'deleted',
        reason,
        previousStatus: debtData.paid || false,
        userEmail,
        timestamp: Timestamp.now()
      });
    } catch (logError) {
      console.warn('Error logging debt deletion (non-critical):', logError);
    }

    return { success: true, message: 'Debt deleted successfully' };
  } catch (error) {
    console.error('❌ Error deleting operator debt:', error);
    throw error;
  }
};

export const getUnreadNotifications = async () => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting notifications:', error);
    return [];
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const notifRef = doc(db, 'notifications', notificationId);
    await updateDoc(notifRef, {
      read: true,
      readAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }
};

// ============================================================
// ✅ FUNCIONES DE CONFIGURACIÓN GLOBAL (EMPRESAS, AJUSTADORES, CAUSAS)
// ============================================================
const SETTINGS_COLLECTION = 'config';
const SETTINGS_DOCUMENT = 'claims_settings';

export const getGlobalSettings = async () => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOCUMENT);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return {
      companiesList: ['Compañía de Ejemplo'],
      adjustersByCompany: { 'Compañía de Ejemplo': ['Ajustador de Ejemplo'] },
      causesList: ['Water Damage', 'Fire Damage', 'Storm Damage', 'Theft', 'Other']
    };
  } catch (error) {
    console.error('Error getting global settings:', error);
    return null;
  }
};

export const saveGlobalSettings = async (settingsData) => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOCUMENT);
    await updateDoc(docRef, {
      ...settingsData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    if (error.code === 'not-found' || error.message.includes('No document to update')) {
      const { setDoc } = await import('firebase/firestore');
      const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOCUMENT);
      await setDoc(docRef, {
        ...settingsData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } else {
      console.error('Error saving global settings:', error);
      throw error;
    }
  }
};

// Asignar el objeto a una constante antes de la exportación por defecto (Arreglo Warning ESLint)
const claimService = {
  createClaim,
  updateClaim,
  deleteClaim,
  getAllClaims,
  getClaimById,
  getClaimsByCompany,
  getClaimsByAdjuster,
  getClaimsByStatus,
  getClaimsByPaymentStatus,
  updateAttachmentStatus,
  markAsPaid,
  updateClaimStatus,
  getStatistics,
  getLastInvoiceNumber,
  searchClaims,
  getOperatorDebts,
  getOperatorDebtByInvoice,
  updateOperatorDebtPayment,
  createOperatorDebt,
  deleteOperatorDebt,
  getOperatorDebtSummary,
  regenerateMissingDebts,
  auditOperatorDebts,
  cleanupPrematureOperatorDebts,
  getDebtPaymentLogs,
  getUnreadNotifications,
  markNotificationAsRead,
  getGlobalSettings,
  saveGlobalSettings
};

export default claimService;

// ── Espejo de cheques para el CSR Assistant (utils/checkSummary.js) ───────

// Cheques de varias compañías (el CSR Assistant puede tener más de una): una
// consulta por compañía, que es lo que las reglas pueden autorizar.
export const getCheckSummariesByCompanies = async companies => {
  const lists = await Promise.all((companies || []).map(async companyName => {
    const snapshot = await getDocs(query(collection(db, CHECK_SUMMARIES_COLLECTION), where('companyName', '==', companyName)));
    return snapshot.docs.map(item => withCrmLink({ id: item.id, ...item.data() }));
  }));
  return mergeById(lists.flat());
};

export const getCheckSummaryById = async checkId => {
  const snapshot = await getDoc(doc(db, CHECK_SUMMARIES_COLLECTION, checkId));
  return snapshot.exists() ? withCrmLink({ id: snapshot.id, ...snapshot.data() }) : null;
};

// Una sola vez (y cuando haga falta rehacerlo): crea/actualiza el espejo de
// TODOS los cheques. Solo el Super Admin (lee `checks` completo). Por lotes.
export const backfillCheckSummaries = async () => {
  const snapshot = await getDocs(collection(db, CHECKS_COLLECTION));
  const docs = snapshot.docs;
  for (let i = 0; i < docs.length; i += 400) {
    const batch = writeBatch(db);
    docs.slice(i, i + 400).forEach(item => {
      batch.set(doc(db, CHECK_SUMMARIES_COLLECTION, item.id), buildCheckSummary(item.data()));
    });
    await batch.commit();
  }
  return { checks: docs.length };
};

// ── Corrección de texto en "Payable to" (Herramientas) ─────────────────────

// Busca (SIN escribir) los cheques cuyo `payableTo` cambiaría al reemplazar `find` por `replacement`.
// Solo el Super Admin (lee `checks` completo).
export const findPayableToFixes = async (find, replacement) => {
  const snapshot = await getDocs(collection(db, CHECKS_COLLECTION));
  return payableToFixes(snapshot.docs.map(item => ({ id: item.id, ...item.data() })), find, replacement);
};

// Aplica las correcciones ya revisadas: actualiza el cheque y su espejo. Por lotes.
export const applyPayableToFixes = async fixes => {
  for (let i = 0; i < fixes.length; i += 200) {
    const batch = writeBatch(db);
    fixes.slice(i, i + 200).forEach(fix => {
      batch.update(doc(db, CHECKS_COLLECTION, fix.id), { payableTo: fix.next, updatedAt: Timestamp.now() });
      batch.set(doc(db, CHECK_SUMMARIES_COLLECTION, fix.id), { payableTo: fix.next, updatedAt: Timestamp.now() }, { merge: true });
    });
    await batch.commit();
  }
  return { updated: fixes.length };
};
